import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Activity, AlertTriangle, ThumbsDown, Pencil, ShieldCheck,
  Loader2, Bug, Users, ExternalLink, RefreshCw, Github, Copy, Check,
  FileText, Mic, Image as ImageIcon,
} from "lucide-react";
import api from "../lib/api";
import { uploadUrl } from "../lib/uploads";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

/**
 * /settings/hidro/health — owner / admin dashboard surfacing every
 * §20 signal: bug reports, edit-diff offenders, recent role changes.
 *
 * All data comes from existing endpoints already shipped:
 *   GET /api/hidro/bugs/health
 *   GET /api/hidro/bugs?status=…
 *   GET /api/hidro/tools/audit
 */
const STATUS_COLORS = {
  new: "text-rose-300 bg-rose-500/10 border-rose-500/30",
  triaged: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  fixing: "text-[#1FB6C1] bg-[#1FB6C1]/10 border-[#1FB6C1]/30",
  resolved: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  wontfix: "text-[#647087] bg-white/5 border-white/10",
};
const SEVERITY_COLORS = {
  low: "text-[#647087]",
  medium: "text-amber-300",
  high: "text-rose-300",
  critical: "text-rose-400 font-bold",
};

export default function HidroHealth() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("new");
  const [busyBugId, setBusyBugId] = useState(null);
  const [copiedBugId, setCopiedBugId] = useState(null);
  const [expandedBugId, setExpandedBugId] = useState(null);

  const formatBugAsMarkdown = (b) => {
    const att = (b.attachments || [])
      .map((a, i) => `${i + 1}. [${a.filename || a.kind || "file"}](${a.url || ""})`)
      .join("\n");
    const errs = (b.console_errors || []).slice(0, 10).join("\n");
    return [
      `### Bug — ${b.severity?.toUpperCase() || "MEDIUM"}`,
      `**Reported:** ${b.created_at}`,
      `**By:** ${b.user_name || ""} (${b.user_role || ""})`,
      `**Route:** \`${b.route || "—"}\``,
      `**Status:** ${b.status || "new"}`,
      "",
      "**What happened:**",
      b.user_reason || "—",
      ...(att ? ["", "**Attachments:**", att] : []),
      ...(errs ? ["", "**Recent console errors:**", "```", errs, "```"] : []),
      ...(b.user_agent ? ["", `**User agent:** \`${b.user_agent}\``] : []),
    ].join("\n");
  };

  const copyAsMarkdown = async (b) => {
    const md = formatBugAsMarkdown(b);
    try {
      await navigator.clipboard.writeText(md);
      setCopiedBugId(b.id);
      toast.success("Copied — paste it anywhere.");
      setTimeout(() => setCopiedBugId(null), 1800);
    } catch (_e) {
      window.prompt("Copy this:", md);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [h, b] = await Promise.all([
        api.get("/hidro/bugs/health"),
        api.get(`/hidro/bugs?status=${filter}&limit=50`),
      ]);
      setHealth(h.data);
      setBugs(b.data?.items || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Owner / admin only.");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const transition = async (bid, nextStatus) => {
    setBusyBugId(bid);
    try {
      await api.patch(`/hidro/bugs/${bid}`, { status: nextStatus });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Update failed");
    }
    setBusyBugId(null);
  };

  const fileToGithub = async (bid) => {
    setBusyBugId(bid);
    try {
      const { data } = await api.post(`/hidro/bugs/${bid}/github`, {});
      // Show the templated body in a textarea modal so the owner can
      // paste it into the platform's Save-to-GitHub dialog.
      const body = data?.ready_body || "";
      try { await navigator.clipboard?.writeText(body); } catch (_e) { /* noop */ }
      window.alert("Bug body copied to clipboard. Paste it into 'Save to GitHub' from the chat input.");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "GitHub flagging failed");
    }
    setBusyBugId(null);
  };

  if (!user || !["owner", "admin"].includes(user.role)) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to=".." className="hv-btn hv-btn-ghost text-xs"><ArrowLeft className="w-3 h-3" /> Back</Link>
        <div className="mt-4 text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-3 text-sm">
          Owner / admin only.
        </div>
      </div>
    );
  }

  if (loading && !health) {
    return (
      <div className="p-6 text-center text-xs text-[#647087]">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading Hidro health…
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5" data-testid="hidro-health-page">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/settings/hidro" className="hv-btn hv-btn-ghost text-xs" data-testid="health-back">
          <ArrowLeft className="w-3 h-3" /> Back to Hidro settings
        </Link>
        <button onClick={load} className="ml-auto hv-btn hv-btn-ghost text-xs" data-testid="health-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]">Hidro · Health</div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-0.5 inline-flex items-center gap-2">
          <Activity className="w-7 h-7 text-[#C4B5FD]" /> Hidro health dashboard
        </h1>
        <p className="text-sm text-[#a0aabf] mt-1">
          Where Hidro is dropping the ball, what users are flagging,
          and which surfaces need a fix.
        </p>
      </div>

      {err && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">{err}</div>
      )}

      {/* ── KPI tiles ─────────────────────────────────────────────── */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="health-kpis">
        <KPI
          icon={<Bug className="w-4 h-4 text-rose-300" />}
          label="New (7d)"
          value={health?.new_7d ?? "—"}
          tone="rose"
        />
        <KPI
          icon={<AlertTriangle className="w-4 h-4 text-amber-300" />}
          label="Open (7d)"
          value={health?.open_7d ?? "—"}
          tone="amber"
        />
        <KPI
          icon={<Pencil className="w-4 h-4 text-[#A78BFA]" />}
          label="Heavy edit-diff surfaces"
          value={(health?.top_edit_diff_offenders_30d || []).length}
          tone="violet"
        />
        <KPI
          icon={<ShieldCheck className="w-4 h-4 text-emerald-300" />}
          label="Top problem surfaces"
          value={(health?.top_surfaces_30d || []).length}
          tone="emerald"
        />
      </section>

      {/* ── Top problem surfaces (30d) ───────────────────────────── */}
      <section className="hv-card p-4" data-testid="health-top-surfaces">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-300" />
          <h2 className="font-display text-base font-bold">Top problem surfaces (last 30 days)</h2>
        </div>
        {(health?.top_surfaces_30d || []).length === 0 ? (
          <div className="text-[11px] text-[#647087] italic">No bug clusters yet — Hidro is shipping clean.</div>
        ) : (
          <ul className="space-y-1.5">
            {health.top_surfaces_30d.map((s) => (
              <li key={s.surface} className="text-[11px] flex items-center gap-2 border border-white/5 rounded px-2 py-1.5 bg-black/10">
                <span className="font-mono text-[#dde3ec] flex-1">{s.surface}</span>
                <span className="text-amber-300">{s.count} bug{s.count === 1 ? "" : "s"}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Heavy edit-diff offenders ─────────────────────────────── */}
      <section className="hv-card p-4" data-testid="health-edit-diff">
        <div className="flex items-center gap-2 mb-3">
          <Pencil className="w-4 h-4 text-[#A78BFA]" />
          <h2 className="font-display text-base font-bold">Where users rewrite Hidro most (30d)</h2>
        </div>
        {(health?.top_edit_diff_offenders_30d || []).length === 0 ? (
          <div className="text-[11px] text-[#647087] italic">
            No surface stands out. Either Hidro's nailing it — or there's not enough signal yet.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {health.top_edit_diff_offenders_30d.map((s) => (
              <li key={s.surface} className="text-[11px] flex items-center gap-2 border border-white/5 rounded px-2 py-1.5 bg-black/10">
                <span className="font-mono text-[#dde3ec] flex-1">{s.surface}</span>
                <span className="text-[#C4B5FD]">{Math.round((s.avg_edit_diff || 0) * 100)}% avg rewrite</span>
                <span className="text-[#647087]">· {s.count} samples</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Bug triage list ────────────────────────────────────────── */}
      <section className="hv-card p-4" data-testid="health-bug-list">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Bug className="w-4 h-4 text-rose-300" />
          <h2 className="font-display text-base font-bold">Bug triage</h2>
          <div className="ml-auto flex items-center gap-1">
            {["new", "triaged", "fixing", "resolved", "wontfix"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(s)}
                className={`text-[11px] px-2 py-0.5 rounded-full border ${
                  filter === s
                    ? "bg-[#1FB6C1]/15 text-[#1FB6C1] border-[#1FB6C1]/40"
                    : "bg-white/5 text-[#a0aabf] border-white/10 hover:border-[#1FB6C1]/30"
                }`}
                data-testid={`health-filter-${s}`}
              >{s}</button>
            ))}
          </div>
        </div>
        {bugs.length === 0 ? (
          <div className="text-[11px] text-[#647087] italic">No bugs in this status.</div>
        ) : (
          <ul className="space-y-2">
            {bugs.map((b) => (
              <li key={b.id} className="border border-white/10 rounded p-3 bg-black/10" data-testid={`health-bug-${b.id}`}>
                <div className="flex items-start gap-2 flex-wrap">
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${STATUS_COLORS[b.status] || STATUS_COLORS.new}`}>
                    {b.status}
                  </span>
                  <span className={`text-[10px] font-mono uppercase ${SEVERITY_COLORS[b.severity] || ""}`}>
                    {b.severity}
                  </span>
                  <span className="text-[11px] text-[#a0aabf]">{b.surface || "—"}</span>
                  <span className="ml-auto text-[10px] text-[#647087] font-mono">
                    {b.user_name} · {(b.created_at || "").slice(0, 16).replace("T", " ")}
                  </span>
                </div>
                <div className="text-sm text-[#dde3ec] mt-1.5 whitespace-pre-wrap">
                  {expandedBugId === b.id ? (b.user_reason || "(no reason)") : (
                    <span className="line-clamp-3">{b.user_reason || "(no reason)"}</span>
                  )}
                </div>
                {b.route && (
                  <div className="text-[10px] text-[#647087] mt-1 font-mono">{b.route}</div>
                )}
                {/* Attachments */}
                {(b.attachments || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`health-bug-attachments-${b.id}`}>
                    {b.attachments.map((a, i) => {
                      const url = a.url || (a.upload_id ? uploadUrl(a.upload_id) : "");
                      const Icon = a.kind === "image" ? ImageIcon : a.kind === "audio" ? Mic : FileText;
                      return (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#1FB6C1]/10 text-[#1FB6C1] border border-[#1FB6C1]/30 hover:bg-[#1FB6C1]/20 transition"
                        >
                          <Icon className="w-3 h-3" /> {a.filename || a.kind || "file"}
                        </a>
                      );
                    })}
                  </div>
                )}
                {expandedBugId === b.id && (b.console_errors || []).length > 0 && (
                  <div className="mt-2 text-[10px] font-mono bg-black/40 border border-white/10 rounded p-2 max-h-32 overflow-y-auto">
                    <div className="text-[#647087] mb-1 uppercase tracking-wider">Console errors ({b.console_errors.length})</div>
                    {b.console_errors.slice(0, 10).map((e, i) => (
                      <div key={i} className="text-rose-300 truncate">{e}</div>
                    ))}
                  </div>
                )}
                {expandedBugId === b.id && b.user_agent && (
                  <div className="mt-1.5 text-[9px] font-mono text-[#647087] break-all">UA: {b.user_agent}</div>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <button
                    onClick={() => copyAsMarkdown(b)}
                    className="hv-btn hv-btn-ghost text-[10px]"
                    title="Copy this bug as Markdown"
                    data-testid={`health-bug-copy-${b.id}`}
                  >
                    {copiedBugId === b.id ? <Check className="w-3 h-3 text-emerald-300" /> : <Copy className="w-3 h-3" />}
                    {copiedBugId === b.id ? "Copied" : "Copy"}
                  </button>
                  <button
                    onClick={() => setExpandedBugId(expandedBugId === b.id ? null : b.id)}
                    className="hv-btn hv-btn-ghost text-[10px]"
                    data-testid={`health-bug-toggle-${b.id}`}
                  >
                    {expandedBugId === b.id ? "Collapse" : "Expand"}
                  </button>
                  {b.status === "new" && (
                    <button
                      onClick={() => transition(b.id, "triaged")}
                      disabled={busyBugId === b.id}
                      className="hv-btn hv-btn-ghost text-[10px]"
                      data-testid={`health-bug-triage-${b.id}`}
                    >Mark triaged</button>
                  )}
                  {["new", "triaged"].includes(b.status) && (
                    <button
                      onClick={() => transition(b.id, "fixing")}
                      disabled={busyBugId === b.id}
                      className="hv-btn hv-btn-ghost text-[10px]"
                    >Start fixing</button>
                  )}
                  {b.status !== "resolved" && (
                    <button
                      onClick={() => transition(b.id, "resolved")}
                      disabled={busyBugId === b.id}
                      className="hv-btn hv-btn-ghost text-[10px] text-emerald-300"
                      data-testid={`health-bug-resolve-${b.id}`}
                    >Mark complete</button>
                  )}
                  {b.status !== "wontfix" && (
                    <button
                      onClick={() => transition(b.id, "wontfix")}
                      disabled={busyBugId === b.id}
                      className="hv-btn hv-btn-ghost text-[10px] text-[#647087]"
                    >Won't fix</button>
                  )}
                  <button
                    onClick={() => fileToGithub(b.id)}
                    disabled={busyBugId === b.id}
                    className="hv-btn hv-btn-ghost text-[10px] ml-auto"
                    title="Copies a templated bug body to clipboard, ready to paste into Save-to-GitHub."
                    data-testid={`health-bug-github-${b.id}`}
                  >
                    <Github className="w-3 h-3" /> Ready for GitHub
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KPI({ icon, label, value, tone = "violet" }) {
  const ring = {
    rose: "border-rose-500/30 bg-rose-500/[0.03]",
    amber: "border-amber-500/30 bg-amber-500/[0.03]",
    violet: "border-[#A78BFA]/30 bg-[#A78BFA]/[0.03]",
    emerald: "border-emerald-500/30 bg-emerald-500/[0.03]",
  }[tone];
  return (
    <div className={`hv-card p-3 ${ring}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
